import { ComponentProps } from 'react'
import { useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Input, OneModal } from '@factorialco/f0-react/dist/experimental'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import {
  CreateProjectFromQuoteDocument,
  QuotesTabsVisibilityDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'

interface ConvertToProjectModalFormValues {
  name: string
  employeeId: number | undefined
}

interface ConvertToProjectModalProps {
  isOpen: boolean
  onClose: () => void
  currency: string
  quoteName: string
  totalBudget: number
}

export const ConvertToProjectModal = ({
  isOpen,
  onClose,
  currency,
  quoteName,
  totalBudget,
}: ConvertToProjectModalProps) => {
  const { quoteId } = useParams()
  const {
    access: { locale },
  } = useCurrentSession()
  const [createProjectFromQuote] = useMutation(CreateProjectFromQuoteDocument, {
    extractorFn: (data) => data.financeMutations.convertToProjectFinanceQuote,
    refetchQueries: [QuotesTabsVisibilityDocument],
  })

  const details: ComponentProps<typeof DetailsItemsList>['details'] = [
    {
      title: i18n.t('finance.quotes.form.convert_to_project.total_hours'),
      content: {
        text: 'XXXXh',
        type: 'item',
      },
    },
    {
      title: i18n.t('finance.quotes.form.convert_to_project.estimated_cost'),
      content: {
        text: 'XXXXX €',
        type: 'item',
      },
    },
    {
      title: i18n.t('finance.quotes.form.convert_to_project.total_budget'),
      content: {
        text: MoneyWithCurrency({
          optionalDecimals: true,
          amount: totalBudget,
          currency,
          locale,
        }),
        type: 'item',
      },
    },
    {
      title: i18n.t('finance.quotes.form.convert_to_project.profit_margin'),
      content: [
        {
          text: 'XXXXX €',
          type: 'item',
        },
        {
          text: 'XX%',
          type: 'item',
        },
      ],
    },
  ]

  const form = useForm<ConvertToProjectModalFormValues>({
    defaultValues: {
      name: quoteName,
      employeeId: undefined,
    },
    onSubmit: async (_values) => {
      const response = await createProjectFromQuote({
        variables: {
          id: Number(quoteId),
        },
      })

      const handled = handleResult(response, () => undefined)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_saving'),
        })
      }

      onClose()
      return handled
    },
  })

  return (
    <OneModal isOpen={isOpen} onClose={onClose} width="sm">
      <OneModal.Header title={i18n.t('finance.quotes.actions.convert_to_project')} />
      <OneModal.Content withPadding>
        <Box gap="s24" width="full">
          <Input
            required
            label={i18n.t('finance.quotes.form.convert_to_project.project_name')}
            type="text"
            size="md"
            value={form.values.name || ''}
            onChange={(value) => form.setValues({ ...form.values, name: value })}
            error={form.errors.name}
            placeholder={'Name'}
          />
          <EmployeeSelectorV2
            label={i18n.t('finance.quotes.form.convert_to_project.project_owner')}
            onChange={(employeeId) => form.setValues({ ...form.values, employeeId })}
            value={form.values.employeeId}
          />
          <DetailsItemsList
            details={details}
            tableView
            title={i18n.t('finance.quotes.form.convert_to_project.project_summary')}
          />
        </Box>
      </OneModal.Content>
      <OneModal.Footer>
        <Box gap="s8" flexDirection="row" justifyContent="flexEnd" width="full">
          <F0Button label={i18n.t('common.cancel')} onClick={onClose} variant="outline" />
          <F0Button
            label={i18n.t('finance.quotes.actions.create_project')}
            onClick={() => form.handleSubmit()}
          />
        </Box>
      </OneModal.Footer>
    </OneModal>
  )
}
